import { Box, Link, Stack, Typography } from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import { SvgIconComponent } from "@mui/icons-material";

export interface IconBulletItem {
  id: string;
  primary: string;
  secondary?: string;
  url?: string | null;
}

interface IconBulletListProps {
  title: string;
  icon: SvgIconComponent;
  items: IconBulletItem[];
  note?: string;
  emptyText?: string;
  linkOn?: "primary" | "secondary";
}

export default function IconBulletList({
  title,
  icon: Icon,
  items,
  note,
  emptyText,
  linkOn = "primary",
}: IconBulletListProps) {
  const linkSx = {
    color: "#C46A2D",
    textDecorationColor: "rgba(196,106,45,0.4)",
    "&:hover": { color: "#8A4319" },
  };

  const renderText = (text: string, url: string | null | undefined, bold: boolean) => {
    const typography = (
      <Typography
        component="span"
        sx={{
          color: url ? "inherit" : bold ? "#20242A" : "#62605A",
          fontFamily: bold ? "Source Sans Pro SemiBold" : "Source Sans Pro Regular",
          fontSize: { xs: 15, md: 16 },
          lineHeight: 1.5,
        }}
      >
        {text}
      </Typography>
    );

    return url ? (
      <Link href={url} target="_blank" rel="noopener noreferrer" sx={linkSx}>
        {typography}
      </Link>
    ) : (
      typography
    );
  };

  return (
    <Box sx={{ height: "100%", textAlign: "start" }}>
      <Stack alignItems="center" spacing={1.5} sx={{ mb: { xs: 2.5, md: 3.5 } }}>
        <Box
          sx={{
            width: 68,
            height: 68,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            border: "2px solid #E4DED2",
            backgroundColor: "#FFFFFF",
            boxShadow: "0 10px 24px rgba(32,36,42,0.06)",
          }}
        >
          <Icon sx={{ color: "#C46A2D", fontSize: 32 }} />
        </Box>
        <Typography
          component="h3"
          sx={{
            color: "#20242A",
            fontFamily: "Source Sans Pro SemiBold",
            fontSize: { xs: 21, md: 24 },
            lineHeight: 1.2,
            textAlign: "center",
          }}
        >
          {title}
        </Typography>
      </Stack>

      {note ? (
        <Typography
          sx={{
            color: "#62605A",
            fontSize: { xs: 14, md: 15 },
            lineHeight: 1.65,
            mb: 2.5,
          }}
        >
          {note}
        </Typography>
      ) : null}

      {items.length === 0 ? (
        <Typography sx={{ color: "#9A948A", fontSize: { xs: 14, md: 15 }, fontStyle: "italic" }}>
          {emptyText}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {items.map((item) => (
            <Stack key={item.id} direction="row" spacing={1.2} alignItems="flex-start">
              <BoltIcon sx={{ color: "#C46A2D", fontSize: 20, mt: "2px", flex: "0 0 auto" }} />
              <Box sx={{ minWidth: 0 }}>
                {renderText(item.primary, linkOn === "primary" ? item.url : null, true)}
                {item.secondary ? (
                  <Box sx={{ mt: 0.3 }}>
                    {linkOn === "secondary" ? (
                      renderText(item.secondary, item.url, false)
                    ) : (
                      <Typography
                        sx={{ color: "#62605A", fontSize: { xs: 14, md: 15 }, lineHeight: 1.5 }}
                      >
                        {item.secondary}
                      </Typography>
                    )}
                  </Box>
                ) : null}
              </Box>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}
